import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, bytes, relativeTime, type DiscoveredResource, type DiscoveryStats } from "../api";
import { State } from "./Services";

/* The triage queue.
 *
 * This estate has a few hundred guests, most of which are either the
 * platform's own infrastructure or already accounted for. The default view is
 * therefore the handful that still need a decision — a list of everything is
 * not a work queue, it is a haystack.
 */

const VIEWS = [
  { key: "needs_attention", label: "Needs a decision" },
  { key: "claimed", label: "Claimed" },
  { key: "missing", label: "Missing" },
  { key: "all", label: "All" },
] as const;

export function AdminDiscovery() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<(typeof VIEWS)[number]["key"]>("needs_attention");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const stats = useQuery({
    queryKey: ["discovery-stats"],
    queryFn: () => api.get<DiscoveryStats>("/api/admin/discovered/stats"),
  });

  const resources = useQuery({
    queryKey: ["discovered", view, search],
    queryFn: () =>
      api.get<DiscoveredResource[]>(
        `/api/admin/discovered?state=${view}&limit=500${search ? `&q=${encodeURIComponent(search)}` : ""}`,
      ),
  });

  const classify = useMutation({
    mutationFn: (payload: { ids: string[]; classification: string }) =>
      api.post("/api/admin/discovered/classify-bulk", {
        resource_ids: payload.ids,
        classification: payload.classification,
      }),
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["discovered"] });
      queryClient.invalidateQueries({ queryKey: ["discovery-stats"] });
    },
  });

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const rows = resources.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <p className="label">Discovery</p>
          <h1>What we found on the clusters</h1>
          <p className="lede">
            Nothing is attached to a customer automatically. Mark the platform's own machines as
            internal to clear them from this list.
          </p>
        </div>
      </div>

      {stats.data && (
        <div className="panel" style={{ marginBottom: "1rem" }}>
          <dl className="specs">
            <div>
              <dt className="label">Needs a decision</dt>
              <dd style={{ fontSize: "var(--step-2)" }}>{stats.data.needs_attention}</dd>
            </div>
            <div>
              <dt className="label">Claimed</dt>
              <dd style={{ fontSize: "var(--step-2)" }}>{stats.data.claimed}</dd>
            </div>
            <div>
              <dt className="label">Internal</dt>
              <dd style={{ fontSize: "var(--step-2)" }}>{stats.data.internal}</dd>
            </div>
            <div>
              <dt className="label">Missing</dt>
              <dd style={{ fontSize: "var(--step-2)" }}>{stats.data.missing}</dd>
            </div>
            <div>
              <dt className="label">Discovered</dt>
              <dd style={{ fontSize: "var(--step-2)" }}>{stats.data.total}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="toolbar">
        <div className="seg">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={view === v.key}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Find by name, id or node"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: "16rem" }}
        />
        {selected.size > 0 && (
          <div className="btn-group" style={{ marginLeft: "auto" }}>
            <span className="label">{selected.size} selected</span>
            <button
              type="button"
              className="btn"
              disabled={classify.isPending}
              onClick={() =>
                classify.mutate({ ids: [...selected], classification: "internal" })
              }
            >
              Mark internal
            </button>
            <button
              type="button"
              className="btn"
              disabled={classify.isPending}
              onClick={() =>
                classify.mutate({ ids: [...selected], classification: "customer" })
              }
            >
              Mark customer
            </button>
          </div>
        )}
      </div>

      {resources.isPending && <p className="muted">Loading…</p>}

      {rows.length === 0 && !resources.isPending && (
        <div className="empty">
          <h2>{view === "needs_attention" ? "Everything is accounted for" : "Nothing here"}</h2>
          <p>
            {view === "needs_attention"
              ? "Every discovered guest is either claimed by a customer or marked internal."
              : "Try a different view or search."}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: "2.5rem" }}>
                  <span className="label">Sel</span>
                </th>
                <th>Name</th>
                <th>ID</th>
                <th>Node</th>
                <th>Spec</th>
                <th>State</th>
                <th>Owner</th>
                <th>Seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td>
                    <strong>{row.name}</strong>
                    {row.classification === "internal" && (
                      <span className="label" style={{ marginLeft: "0.5rem" }}>
                        internal
                      </span>
                    )}
                  </td>
                  <td className="id">
                    {row.kind === "pve_lxc" ? "ct" : row.kind === "pve_qemu" ? "vm" : "web"}{" "}
                    {row.external_id}
                  </td>
                  <td className="id">{row.node_name}</td>
                  <td className="id">
                    {row.attributes.cpu_cores ?? "—"} vCPU · {bytes(row.attributes.memory_total_bytes)}
                  </td>
                  <td>
                    <State value={row.missing_since ? "missing" : (row.attributes.power_state ?? "unknown")} />
                  </td>
                  <td>{row.org_name ?? <span className="muted">unassigned</span>}</td>
                  <td className="id">{relativeTime(row.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
